import React, { useState, useEffect, useRef } from 'react';
import {
  nextTargetEpoch,
  formatTargetInZone,
  getTimezoneOption,
} from '../lib/timezones';

const parseHHMM = (value) => {
  if (!value) return null;
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { h, m };
};

// LIVE mode: countdown from real-time clock to target HH:MM in the chosen zone.
const calculateLiveRemaining = (targetTime, tzId) => {
  const parsed = parseHHMM(targetTime);
  if (!parsed) return null;
  const targetMs = nextTargetEpoch(parsed.h, parsed.m, tzId);
  return Math.max(0, Math.floor((targetMs - Date.now()) / 1000));
};

// MANUAL mode: compute fixed duration = resume - current. Timezone is irrelevant
// here because both times are interpreted as wall-clock values supplied by the user.
const computeManualDurationSec = (currentTime, targetTime) => {
  const c = parseHHMM(currentTime);
  const t = parseHHMM(targetTime);
  if (!c || !t) return 0;
  let diffMin = (t.h * 60 + t.m) - (c.h * 60 + c.m);
  if (diffMin < 0) diffMin += 24 * 60; // next day
  return diffMin * 60;
};

const pad = (n) => String(n).padStart(2, '0');

export const CountdownTimer = ({
  targetTime,
  targetTimezone = 'local',
  label,
  manualMode,
  manualCurrentTime,
  onZero,
}) => {
  const [totalSeconds, setTotalSeconds] = useState(0);
  // Anchor end-time for manual mode so countdown is stable across renders
  const manualEndRef = useRef(null);
  const manualKey = `${manualCurrentTime}|${targetTime}|${manualMode}`;
  const manualKeyRef = useRef(null);
  const firedZeroRef = useRef(false);

  // Reset the "already fired" flag whenever inputs change so we don't
  // re-fire if the same countdown is restarted with new values.
  useEffect(() => {
    firedZeroRef.current = false;
  }, [targetTime, targetTimezone, manualMode, manualCurrentTime]);

  useEffect(() => {
    const tick = () => {
      let secs;
      if (manualMode) {
        // Reset anchor if inputs changed
        if (manualKeyRef.current !== manualKey) {
          const durationSec = computeManualDurationSec(manualCurrentTime, targetTime);
          manualEndRef.current = Date.now() + durationSec * 1000;
          manualKeyRef.current = manualKey;
        }
        secs = Math.max(0, Math.floor((manualEndRef.current - Date.now()) / 1000));
      } else {
        secs = calculateLiveRemaining(targetTime, targetTimezone) ?? 0;
      }
      setTotalSeconds(secs);

      if (secs === 0 && !firedZeroRef.current) {
        firedZeroRef.current = true;
        if (typeof onZero === 'function') {
          // Fire on next tick so React state has settled
          setTimeout(() => onZero(), 0);
        }
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [targetTime, targetTimezone, manualMode, manualCurrentTime, manualKey, onZero]);

  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const showHours = hrs > 0;

  const parsed = parseHHMM(targetTime);
  const targetDisplay = parsed
    ? formatTargetInZone(parsed.h, parsed.m, manualMode ? 'local' : targetTimezone)
    : '';
  const tz = getTimezoneOption(targetTimezone);
  const showZoneHint = !manualMode && tz.iana && tz.id !== 'local';

  return (
    <div className="countdown-timer" data-testid="countdown-timer">
      {label && <div className="countdown-label">{label}</div>}
      <div className="countdown-display">
        {showHours && (
          <>
            <div className="countdown-unit">
              <span className="countdown-number">{pad(hrs)}</span>
              <span className="countdown-suffix">h</span>
            </div>
            <span className="countdown-separator">:</span>
          </>
        )}
        <div className="countdown-unit">
          <span className="countdown-number">{pad(mins)}</span>
          <span className="countdown-suffix">m</span>
        </div>
        <span className="countdown-separator">:</span>
        <div className="countdown-unit">
          <span className="countdown-number">{pad(secs)}</span>
          <span className="countdown-suffix">s</span>
        </div>
      </div>
      <div className="countdown-target">
        {totalSeconds === 0 ? 'Resuming now' : `Resumes at ${targetDisplay}`}
      </div>
      {showZoneHint && totalSeconds > 0 && (
        <div className="countdown-zone-hint">{tz.label}</div>
      )}
    </div>
  );
};

export default CountdownTimer;
